import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getSuppliers, getSupplierReviews } from '../services/api';
import { SUPPLIER_CATEGORIES } from '../models/supplierModel';
import { CITIES } from '../models/cityModel';

const inp = 'px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-blue-600 transition';
const initials = (name) =>
  (name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

export default function SuppliersPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [cat,       setCat]       = useState('Tüm Kategoriler');
  const [city,      setCity]      = useState('');
  const [sort,      setSort]      = useState('rating');
  const [detail,    setDetail]    = useState(null);
  const [reviews,   setReviews]   = useState(null);   // { average, count, reviews }

  useEffect(() => {
    getSuppliers()
      .then(r => setSuppliers(r.data))
      .catch(() => setError('Tedarikçiler yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  // Load the selected supplier's real reviews when the modal opens.
  const openDetail = (s) => {
    setDetail(s);
    setReviews(null);
    getSupplierReviews(s.id).then(r => setReviews(r.data)).catch(() => setReviews({ reviews: [] }));
  };

  const filtered = useMemo(() => {
    let list = suppliers.filter(s => {
      const ms = !search
        || (s.firm_name || '').toLowerCase().includes(search.toLowerCase())
        || (s.city || '').toLowerCase().includes(search.toLowerCase());
      const mc = cat === 'Tüm Kategoriler' || s.category === cat;
      const mv = !city || s.city === city;
      return ms && mc && mv;
    });
    if (sort === 'rating')  list = [...list].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    if (sort === 'reviews') list = [...list].sort((a, b) => b.review_count - a.review_count);
    return list;
  }, [suppliers, search, cat, city, sort]);

  return (
    <div className="min-h-screen bg-gray-50"><Navbar />

      {/* ── HEADER ── */}
      <div className="bg-white border-b border-gray-100 pt-20 sm:pt-24 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <button onClick={() => navigate('/')} className="hover:text-blue-700 transition">Ana Sayfa</button>
            <span>›</span><span className="text-gray-600">Tedarikçiler</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            Yetkili Oto Yedek Parça Tedarikçileri
          </h1>
          <p className="text-gray-500 text-sm max-w-2xl mb-4">
            Türkiye genelinde onaylı oto yedek parça tedarikçileri. Şehir, kategori ve puana göre filtreleyin.
          </p>
          <div className="flex flex-wrap gap-4">
            {['✓ Admin Onaylı Tedarikçiler','✓ Türkiye Geneli','✓ Vergi Levhası Kontrolü'].map(t => (
              <span key={t} className="text-xs text-gray-500">{t}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">

        {/* ── FILTERS ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <input type="text" className={`${inp} w-full`}
              placeholder="🔍  Tedarikçi veya şehir ara..."
              value={search} onChange={e => setSearch(e.target.value)} />
            <select className={`${inp} w-full`} value={cat} onChange={e => setCat(e.target.value)}>
              {SUPPLIER_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <select className={`${inp} w-full`} value={city} onChange={e => setCity(e.target.value)}>
              <option value="">Tüm Şehirler</option>
              {CITIES.slice(0, 30).map(c => <option key={c}>{c}</option>)}
            </select>
            <select className={`${inp} w-full`} value={sort} onChange={e => setSort(e.target.value)}>
              <option value="rating">Puana Göre Sırala</option>
              <option value="reviews">Değerlendirmeye Göre</option>
            </select>
          </div>
          {!loading && <p className="text-xs text-gray-400 mt-3">{filtered.length} tedarikçi bulundu</p>}
        </div>

        {/* ── TABLE ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <div className="col-span-1">Logo</div>
            <div className="col-span-4">Firma Adı</div>
            <div className="col-span-2">Şehir</div>
            <div className="col-span-2">Uzmanlık</div>
            <div className="col-span-2">Puan</div>
            <div className="col-span-1"></div>
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-400 text-sm">Yükleniyor...</div>
          ) : error ? (
            <div className="text-center py-16 text-red-500 text-sm">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              {suppliers.length === 0
                ? 'Henüz onaylı tedarikçi bulunmuyor.'
                : 'Arama kriterlerinize uygun tedarikçi bulunamadı.'}
            </div>
          ) : filtered.map(s => (
            <div key={s.id}
              className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-4 md:px-6 py-4 border-b border-gray-50 hover:bg-blue-50/30 transition items-center cursor-pointer"
              onClick={() => openDetail(s)}>

              <div className="hidden md:flex col-span-1 items-center">
                <div className="w-10 h-10 rounded-xl bg-blue-700 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {initials(s.firm_name)}
                </div>
              </div>

              <div className="col-span-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-700 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 md:hidden">
                    {initials(s.firm_name)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-gray-900">{s.firm_name}</span>
                    {/* Only when the admin actually checked their vergi levhası */}
                    {s.verified && (
                      <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded"
                        title="Vergi levhası OtoParçamıBul tarafından kontrol edildi">✓ Doğrulanmış</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-span-2">
                <span className="text-sm text-gray-700">📍 {s.city || '—'}</span>
              </div>

              <div className="col-span-2">
                <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-lg">{s.category || 'Genel'}</span>
              </div>

              <div className="col-span-2">
                {s.rating != null ? (
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-yellow-400 text-sm">★</span>
                    <span className="text-sm font-semibold text-gray-800">{s.rating}</span>
                    <span className="text-xs text-gray-400">({s.review_count})</span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 mb-1 inline-block">Yeni — puan yok</span>
                )}
              </div>

              <div className="col-span-1 flex justify-end" onClick={e => e.stopPropagation()}>
                <button onClick={() => navigate('/request')}
                  className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold px-3 py-2 rounded-lg transition whitespace-nowrap">
                  Talep Gönder
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ── CTA ── */}
        <div className="mt-8 bg-blue-700 rounded-2xl p-8 text-center">
          <h3 className="text-white text-xl font-bold mb-2">Tedarikçi misiniz?</h3>
          <p className="text-blue-200 text-sm mb-5 max-w-lg mx-auto">
            Müşterilere ulaşmak için OtoParçamıBul ağına katılın. Kayıt tamamen ücretsiz.
          </p>
          <button onClick={() => navigate('/register')}
            className="bg-white text-blue-700 font-semibold px-6 py-3 rounded-xl text-sm hover:bg-blue-50 transition">
            Ücretsiz Tedarikçi Kaydı →
          </button>
        </div>
      </div>

      {/* ── SUPPLIER DETAIL MODAL ── */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-7 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-blue-700 text-white text-lg font-bold flex items-center justify-center">{initials(detail.firm_name)}</div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-900">{detail.firm_name}</h3>
                    {detail.verified && (
                      <span className="bg-green-50 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-green-200"
                        title="Vergi levhası OtoParçamıBul tarafından kontrol edildi">✓ Doğrulanmış</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">📍 {detail.city || '—'}</p>
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-300 hover:text-gray-500 text-xl leading-none">✕</button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              {[
                ['Uzmanlık', detail.category || 'Genel'],
                ['Puan',     detail.rating != null ? `★ ${detail.rating} (${detail.review_count})` : 'Yeni'],
              ].map(([k, v]) => (
                <div key={k} className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-400 mb-0.5">{k}</p>
                  <p className="text-sm font-medium text-gray-800">{v}</p>
                </div>
              ))}
            </div>

            {/* Reviews */}
            <div className="mb-5">
              <p className="text-xs text-gray-400 mb-2 font-semibold uppercase tracking-wide">Değerlendirmeler</p>
              {reviews === null ? (
                <p className="text-xs text-gray-400">Yükleniyor...</p>
              ) : reviews.reviews.length === 0 ? (
                <p className="text-xs text-gray-400">Henüz değerlendirme yok.</p>
              ) : (
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {reviews.reviews.map(rv => (
                    <div key={rv.id} className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-yellow-400">{'★'.repeat(rv.rating)}<span className="text-gray-200">{'★'.repeat(5 - rv.rating)}</span></span>
                        <span className="text-xs text-gray-400">{rv.customer_name}</span>
                      </div>
                      {rv.comment && <p className="text-xs text-gray-600">{rv.comment}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400 mb-4">
              Bu tedarikçiyle iletişim, talebinize gelen teklif üzerinden platform içinde güvenle yapılır.
            </p>

            <button onClick={() => { setDetail(null); navigate('/request'); }}
              className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold py-2.5 rounded-xl text-sm transition">
              Parça Talebi Gönder
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
